import type { FormInstance } from 'antd/lib/form';

import { captureException } from '@sentry/react';
import { BASEURL, ENDPOINTS, SPXAPI, serverSignUp } from '@/api';
import type {
    CognitoAuthError,
    OTPAuthType,
    PhoneIdentity,
} from '@/types/types';
import { CognitoErrorCodes } from '@/types/types';
import { StatusMessage } from '../../Common/CodeAuthenticator';
import {
    captureErrorForSentry,
    retryRequest,
    checkIsNetworkError,
    authErrorMessageToI18nPayload,
    getErrorResponse,
    checkIsSpecificCode,
    caseInsensitiveUrlParam,
    tryGetTimelyRedirectPath,
    getCognitoErrorName,
} from '../../utils';
import { codeAuthNameSpace, COGNITO_CALL_RETRIES, LAMBDA_TIMEOUT_MESSAGE_PARTIAL } from './constants';

export const getLinkIdFromLocation = (search?: string): string => (
    caseInsensitiveUrlParam(new URLSearchParams(search ?? window.location.search), 'linkid') ?? ''
);

/** Resolve share linkId from the current URL or a saved post-login redirect path. */
export const getLinkIdForSignup = (): string => {
    const fromLocation = getLinkIdFromLocation();
    if (fromLocation) {
        return fromLocation;
    }
    const redirectPath = tryGetTimelyRedirectPath();
    if (!redirectPath) {
        return '';
    }
    const queryIndex = redirectPath.indexOf('?');
    if (queryIndex === -1) {
        return '';
    }
    return caseInsensitiveUrlParam(new URLSearchParams(redirectPath.slice(queryIndex + 1)), 'linkid') ?? '';
};

export interface TrySignUpOptions {
    linkId?: string;
    allowRedirectPathFallback?: boolean;
}

export interface GenerateCodeResult {
    email?: string;
    challengeId?: string;
}

export const generateCode = async (
    APIInstance: SPXAPI, emailLower: string, authType: OTPAuthType, phoneIdentity?: PhoneIdentity,
): Promise<GenerateCodeResult> => {
    const payload = phoneIdentity ? { body: phoneIdentity } : {};
    const linkId = getLinkIdFromLocation();
    const response = await retryRequest<GenerateCodeResult>(
        async (): Promise<GenerateCodeResult> => (
            APIInstance.post(BASEURL.backend(), ENDPOINTS.generateCode(emailLower, authType, linkId), payload)
        ),
        (error) => {
            captureException(error);
            return checkIsNetworkError(error);
        },
        COGNITO_CALL_RETRIES,
    );
    const resolved = response?.email;
    const challengeId = response?.challengeId;
    return {
        email: typeof resolved === 'string' && resolved ? resolved.toLowerCase() : undefined,
        challengeId: typeof challengeId === 'string' && challengeId ? challengeId : undefined,
    };
};

export const filterCognitoError = (error: CognitoAuthError): boolean => {
    const errorName = getCognitoErrorName(error);
    let result = errorName === CognitoErrorCodes.NetworkError;
    if (!result) {
        result = (
            errorName === CognitoErrorCodes.UnexpectedLambdaException
            && error.message?.toLowerCase().includes(LAMBDA_TIMEOUT_MESSAGE_PARTIAL)
        );
    }
    return result;
};

// Narrower retry filter for `Auth.signIn` during the OTP flow.
// We deliberately do NOT retry on Cognito lambda socket-timeout here, because
// the underlying `create_auth_challenge` lambda may have already successfully
// dispatched the OTP email/SMS via Courier and only blown past Cognito's 5s
// invocation ceiling. Retrying then re-invokes `create_auth_challenge` while
// the first invocation is still mid-flight — `recent_otp.sent` has not been
// persisted yet, so the second invocation also calls `send_code_to_user`,
// producing the duplicate email reported by recipients.
// Genuine network failures still retry.
export const filterCognitoSignInRetryError = (error: CognitoAuthError): boolean => (
    getCognitoErrorName(error) === CognitoErrorCodes.NetworkError
);

export class InvalidLinkError extends Error {
    constructor() {
        super('Invalid link');
        this.name = 'InvalidLinkError';
    }
}

export const trySignUp = async (
    usernameLower: string,
    APIInstance: SPXAPI,
    options: TrySignUpOptions = {},
): Promise<void> => {
    const { linkId: resolvedLinkId, allowRedirectPathFallback = true } = options;
    const linkId = resolvedLinkId || (allowRedirectPathFallback ? getLinkIdForSignup() : '');
    try {
        await retryRequest(
            async () => serverSignUp(APIInstance, {
                email: usernameLower,
                clientMetadata: { source: 'otp-recipient' },
                ...(linkId ? { linkId } : {}),
            }),
            (error: unknown): boolean => checkIsNetworkError(error),
            COGNITO_CALL_RETRIES,
        );
    } catch (error) {
        if (checkIsSpecificCode(error, 403)) {
            throw new InvalidLinkError();
        }
        const isExistingUser = checkIsSpecificCode(error, 409)
            || getCognitoErrorName(error) === CognitoErrorCodes.UsernameExistsException
            || (error as CognitoAuthError).message?.toLowerCase()?.includes('user with this email already exists');
        if (!isExistingUser) {
            console.log('sign up fail', error);
            throw error;
        }
    }
};

export const getEmail = (formInstance: FormInstance): string => {
    const { getFieldValue } = formInstance;
    return getFieldValue('email').toLowerCase();
};

const checkIsUserNotFound = (error: unknown): boolean => (
    getCognitoErrorName(error) === CognitoErrorCodes.UserNotFoundException
);

type ErrorMessagePayload = Omit<StatusMessage, 'hasError'>;

const createErrorState = (error: unknown, isNotFound: boolean,
    isSignUpForbidden: boolean = false): ErrorMessagePayload => {
    const errorMessage = getErrorResponse(error)?.message || (error as CognitoAuthError)?.message;
    let i18nKey = '';
    if (isNotFound) {
        i18nKey = 'userNotFound';
    } else if (isSignUpForbidden) {
        i18nKey = 'signUpForbidden';
    } else {
        i18nKey = 'couldNotSendCode';
    }
    const [message, options] = authErrorMessageToI18nPayload(errorMessage, () => [
        `${codeAuthNameSpace}.firstStepCommon.${i18nKey}`,
    ]);
    return { message, options };
};

const checkIsSignUpForbidden = (error: unknown): boolean => {
    const errorResponse = getErrorResponse(error);

    return errorResponse.statusCode === 403 && errorResponse.message.includes('User is not registered yet.');
};

export const onSendCodeError = (error: unknown, setErrorMessage: (messages: StatusMessage[]) => void): void => {
    const isNotFound = checkIsUserNotFound(error);
    const isSignUpForbidden = checkIsSignUpForbidden(error);
    setErrorMessage([{
        ...createErrorState(error, isNotFound, isSignUpForbidden),
        hasError: true,
    }]);
    captureErrorForSentry(error, 'useOTPFlow.onSendCodeError', { severityLevel: isNotFound ? 'warning' : 'error' });
};

export const getPhoneValues = (formInstance: FormInstance): PhoneIdentity => {
    const { getFieldValue } = formInstance;
    return {
        prefix: getFieldValue('prefix').prefix,
        phone: getFieldValue('phone'),
    };
};
